"use client"

import { motion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Phone, Printer, Mail, MapPin } from "lucide-react"

const contactInfo = [
  {
    icon: Phone,
    label: "Phone Number",
    value: "(513) 979-4101",
    href: "tel:+15139794101",
  },
  {
    icon: Mail,
    label: "General Inquiries",
    value: "contact@goodthangzcookin.com",
    href: "mailto:contact@goodthangzcookin.com",
  },
  {
    icon: MapPin,
    label: "Address",
    value: "102 Mill Street, Lockland, OH 45215",
    href: "https://maps.google.com/?q=102+Mill+Street,+Lockland,+OH+45215",
  },
  {
    icon: Printer,
    label: "Fax Inquiries",
    value: "(513) 956-3764",
    href: null,
  },
  {
    icon: Mail,
    label: "Chef Allen",
    value: "chefallen@goodthangzcookin.com",
    href: "mailto:chefallen@goodthangzcookin.com",
  },
  {
    icon: Mail,
    label: "Sous Chef Walt",
    value: "chefwalt@goodthangzcookin.com",
    href: "mailto:chefwalt@goodthangzcookin.com",
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
}

export default function ContactPage() {
  return (
    <div className="min-h-screen py-12 md:py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-balance text-foreground mb-4 font-[family-name:var(--font-display)]">
            Get In Touch
          </h1>
          <p className="text-lg text-muted-foreground text-pretty max-w-2xl mx-auto">
            Have questions or want to place a special order? We'd love to hear from you. Reach out to our team using any
            of the methods below.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto"
        >
          {contactInfo.map((item, index) => {
            const Icon = item.icon
            const content = (
              <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 flex flex-col items-center text-center space-y-4">
                  <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-foreground mb-2">{item.label}</h3>
                    <p className="text-muted-foreground text-pretty">{item.value}</p>
                  </div>
                </CardContent>
              </Card>
            )

            return (
              <motion.div key={index} variants={itemVariants}>
                {item.href ? (
                  <a
                    href={item.href}
                    target={item.href.startsWith("http") ? "_blank" : undefined}
                    rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="block h-full"
                  >
                    {content}
                  </a>
                ) : (
                  content
                )}
              </motion.div>
            )
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 max-w-4xl mx-auto"
        >
          <Card>
            <CardContent className="p-0">
              <div className="aspect-video w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3089.8!2d-84.4574!3d39.2292!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s102%20Mill%20Street%2C%20Lockland%2C%20OH%2045215!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Good Thangz Cookin Location"
                  className="rounded-lg"
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}
